import type { ErrorInfo, ReactNode } from 'react';
import React from 'react';

import Logistics from '../../api/logistics';
import ErrorPage from '../ErrorPage';

const sanitizeStack = (stack: string | null | undefined, maxLength: number) =>
  (stack ?? '')
    .replace(/\b(?:https?|file):\/\/[^\s)]+/g, (value) => {
      try {
        const url = new URL(value);
        return `${url.protocol}//${url.host}${url.pathname}`;
      } catch {
        return value.split(/[?#]/)[0];
      }
    })
    .slice(0, maxLength);

interface ErrorBoundaryState {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<
  { children?: ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children?: ReactNode }) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    const loadingChunkError =
      (error.message.includes('Loading chunk') ||
        error.message.includes('Loading CSS chunk')) &&
      error.message.includes('failed');

    if (!loadingChunkError) {
      Logistics.error({
        uri: window.location.pathname,
        message: error.message,
        stack: sanitizeStack(error.stack, 1000),
        componentStack: sanitizeStack(errorInfo.componentStack, 500),
      });
    }
  }

  render() {
    if (this.state.errorInfo) {
      return <ErrorPage />;
    }
    // Normally, just render children
    return this.props.children;
  }
}
